import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Node build of csv-parse references Buffer at module load (unsafe in browser).
// Use the package's browser bundle (inlines buffer handling).
import { parse } from 'csv-parse/browser/esm/sync';
import { Link2, Pencil, Trash2, X } from 'lucide-react';
import { SYMBOL_OPTIONS } from './symbols/registry';

const CSV_IMPORT_FIELD_ORDER = [
  { key: 'id', label: 'Row ID (updates)' },
  { key: 'plant_id', label: 'PlantADay plant ID' },
  { key: 'common_name', label: 'Common name' },
  { key: 'botanical_name', label: 'Botanical name' },
  { key: 'code', label: 'Code / SKU' },
  { key: 'pot_size', label: 'Pot / container size' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'color', label: 'Colour' },
  { key: 'unit_price', label: 'Unit price' },
  { key: 'width', label: 'Width' },
  { key: 'height', label: 'Height' },
  { key: 'exposure', label: 'Exposure / sun' },
  { key: 'plant_type', label: 'Plant type' },
  { key: 'image_url', label: 'Image URL (direct photo)' },
  { key: 'reference_url', label: 'Reference URL (e.g. Google link)' },
  { key: 'notes', label: 'Notes' }
];

function emptyCsvColumnMapping() {
  return {
    id: '',
    plant_id: '',
    common_name: '',
    botanical_name: '',
    pot_size: '',
    color: '',
    unit_price: '',
    width: '',
    height: '',
    exposure: '',
    plant_type: '',
    code: '',
    notes: '',
    image_url: '',
    reference_url: '',
    quantity: ''
  };
}

/** @param {string[]} headers */
function suggestCsvColumnMapping(headers) {
  const m = emptyCsvColumnMapping();
  if (!headers || !headers.length) return m;
  const n = (h) => String(h || '').trim().toLowerCase();
  const used = new Set();

  const pick = (pred) => {
    for (const h of headers) {
      if (used.has(h)) continue;
      const s = n(h);
      if (!s) continue;
      if (pred(s, h)) {
        used.add(h);
        return h;
      }
    }
    return '';
  };

  m.common_name = pick(
    (s) =>
      s === 'common_name' ||
      s === 'name' ||
      s.includes('plant name') ||
      s === 'common' ||
      s === 'description' ||
      s.includes('common name')
  );
  m.botanical_name = pick(
    (s) =>
      s === 'botanical_name' ||
      s === 'botanical' ||
      s.includes('botanical') ||
      s === 'latin' ||
      s.includes('scientific')
  );
  m.code = pick(
    (s) =>
      s === 'code' ||
      s === 'sku' ||
      s.includes('sku') ||
      s.includes('plant #') ||
      s.includes('plant number') ||
      s === 'number' ||
      s.includes('item code') ||
      (s.endsWith(' #') && !s.includes('plant name'))
  );
  m.pot_size = pick(
    (s) =>
      s === 'pot_size' ||
      s === 'size' ||
      s.includes('pot size') ||
      s.includes('container') ||
      s === 'pot'
  );
  m.quantity = pick((s) => s === 'quantity' || s === 'qty' || s === 'q');
  m.color = pick((s) => s === 'color' || s === 'colour' || s.includes('color'));
  m.unit_price = pick(
    (s) => s === 'unit_price' || s.includes('price') || s === 'unitprice' || s.includes('cost')
  );
  m.width = pick((s) => s === 'width' || s === 'w');
  m.height = pick((s) => s === 'height' || s === 'h');
  m.exposure = pick((s) => s === 'exposure' || s.includes('expo') || s.includes('sun'));
  m.plant_type = pick((s) => s === 'plant_type' || s === 'type' || s.includes('plant type'));
  m.plant_id = pick((s) => s === 'plant_id' || s === 'plant id' || s.includes('plantaday id'));
  m.id = pick(
    (s) =>
      s === 'id' ||
      s === 'item_id' ||
      s === 'item id' ||
      s === 'library id' ||
      s.includes('master id')
  );
  m.image_url = pick(
    (s) =>
      (s.includes('image') && !s.includes('google')) ||
      s.includes('photo') ||
      s.includes('picture') ||
      s === 'img' ||
      s.includes('thumbnail')
  );
  m.reference_url = pick(
    (s) =>
      s.includes('google') ||
      s.includes('reference') ||
      (s.includes('link') && !s.includes('image')) ||
      (s.includes('url') && !m.image_url && !s.includes('image'))
  );
  m.notes = pick((s) => s === 'notes' || s === 'note' || s.includes('comment'));

  return m;
}

function rowToLibraryItemFromMapping(row, mapping) {
  const get = (fieldKey) => {
    const src = mapping[fieldKey];
    if (!src) return '';
    const v = row[src];
    return v != null ? String(v) : '';
  };

  const id = get('id');
  const plant_id = get('plant_id');
  const common_name = get('common_name');
  const botanical_name = get('botanical_name');
  const pot_size = get('pot_size');
  const color = get('color');
  const unit_price = get('unit_price');
  const width = get('width');
  const height = get('height');
  const exposure = get('exposure');
  const plant_type = get('plant_type');
  const code = get('code');
  const notes = get('notes');
  const image_url = get('image_url');
  const reference_url = get('reference_url');
  const quantity = get('quantity');

  return {
    id: String(id || '').trim() || null,
    plant_id: String(plant_id || '').trim() || null,
    common_name: String(common_name || '').trim(),
    botanical_name: botanical_name ? String(botanical_name).trim() : '',
    pot_size: pot_size ? String(pot_size).trim() : '',
    color: color ? String(color).trim() : '',
    unit_price: (() => {
      const s = String(unit_price || '').trim();
      if (!s) return null;
      const x = parseFloat(s);
      return Number.isNaN(x) ? null : x;
    })(),
    width: (() => {
      const s = String(width || '').trim();
      if (!s) return null;
      const x = parseFloat(s);
      return Number.isNaN(x) ? null : x;
    })(),
    height: (() => {
      const s = String(height || '').trim();
      if (!s) return null;
      const x = parseFloat(s);
      return Number.isNaN(x) ? null : x;
    })(),
    exposure: exposure ? String(exposure).trim() : '',
    plant_type: plant_type ? String(plant_type).trim() : '',
    code: code ? String(code).trim() : '',
    notes: notes ? String(notes).trim() : '',
    image_url: image_url ? String(image_url).trim() : '',
    reference_url: reference_url ? String(reference_url).trim() : '',
    quantity: (() => {
      const s = String(quantity || '').trim();
      if (!s) return 1;
      const x = parseInt(s, 10);
      return Number.isNaN(x) ? 1 : x;
    })()
  };
}

function DesignerMasterPlantList() {
  const navigate = useNavigate();
  const location = useLocation();
  const didAutoEditRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [designerSymbols, setDesignerSymbols] = useState([]);
  const allSymbolOptions = useMemo(() => {
    const builtIn = Array.isArray(SYMBOL_OPTIONS) ? SYMBOL_OPTIONS : [];
    const builtInIds = new Set(builtIn.map((o) => String(o.id)));
    const custom = (Array.isArray(designerSymbols) ? designerSymbols : [])
      .map((s) => ({
        id: String(s?.slug || '').trim().toLowerCase(),
        label: String(s?.label || s?.slug || '').trim() || 'Custom symbol'
      }))
      .filter((o) => o.id && !builtInIds.has(o.id));
    return [...builtIn, ...custom];
  }, [designerSymbols]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageSize, setPageSize] = useState(200); // server caps at 500
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [deduping, setDeduping] = useState(false);

  // Multi-select + bulk symbol assignment
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkSymbolModal, setShowBulkSymbolModal] = useState(false);
  const [bulkSymbol, setBulkSymbol] = useState('circle');
  const [bulkSymbolSizeIn, setBulkSymbolSizeIn] = useState('0.5'); // inches (library is canonical)
  const [bulkApplyingSymbol, setBulkApplyingSymbol] = useState(false);

  useEffect(() => {
    // Load designer symbol library for dropdown options
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const API_BASE_URL = process.env.REACT_APP_API_URL ||
          (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
        const resp = await fetch(`${API_BASE_URL}/designer-symbols`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.ok) setDesignerSymbols(Array.isArray(data.symbols) ? data.symbols : []);
      } catch {
        // ignore
      }
    })();
  }, []);

  const initialSearch = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search);
      return params.get('search') || '';
    } catch {
      return '';
    }
  }, [location.search]);

  const [search, setSearch] = useState(initialSearch);
  const [missingFilter, setMissingFilter] = useState(''); // '', 'common_name', 'botanical_name', 'image_url', 'code'
  const [filterExposure, setFilterExposure] = useState(''); // '' | '__missing__' | normalized value
  const [filterType, setFilterType] = useState(''); // '' | '__missing__' | normalized value
  const [filterColour, setFilterColour] = useState(''); // '' | '__missing__' | normalized value

  // Consolidation
  const [consolidating, setConsolidating] = useState(false);

  // CSV import (file → column map → preview → import)
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvImportStep, setCsvImportStep] = useState(1);
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvColumnMapping, setCsvColumnMapping] = useState(() => emptyCsvColumnMapping());
  const [csvParsing, setCsvParsing] = useState(false);
  const [importing, setImporting] = useState(false);

  // Manual add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newValues, setNewValues] = useState({
    plant_id: null,
    common_name: '',
    botanical_name: '',
    pot_size: '',
    color: '',
    unit_price: '',
    width: '',
    height: '',
    exposure: '',
    plant_type: '',
    code: '',
    quantity: 1,
    notes: '',
    image_url: '',
    reference_url: ''
  });
  const [creating, setCreating] = useState(false);

  // Add modal: DB search + photo picker
  const [addSearchTerm, setAddSearchTerm] = useState('');
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [addSelectedPlant, setAddSelectedPlant] = useState(null);
  const [addPhotosLoading, setAddPhotosLoading] = useState(false);
  const [addPhotosError, setAddPhotosError] = useState('');
  const [addPhotoOptions, setAddPhotoOptions] = useState([]); // [{url, thumbnail, attribution, source, verified, ...}]
  const [addSelectedPhotoUrl, setAddSelectedPhotoUrl] = useState('');

  // Add modal: AI lookup -> create private plant -> add to designer library
  const [aiLookupQuery, setAiLookupQuery] = useState('');
  const [aiLookupLoading, setAiLookupLoading] = useState(false);
  const [aiLookupError, setAiLookupError] = useState('');
  const [aiDraftPlant, setAiDraftPlant] = useState(null);
  const [aiSmartResults, setAiSmartResults] = useState([]);
  const [aiPhotosLoading, setAiPhotosLoading] = useState(false);
  const [aiPhotosError, setAiPhotosError] = useState('');
  const [aiPhotoOptions, setAiPhotoOptions] = useState([]);
  const [aiSelectedPhotoUrl, setAiSelectedPhotoUrl] = useState('');
  const [addingAIPlant, setAddingAIPlant] = useState(false);

  // Edit modal
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Edit modal photo picker
  const [editPhotosLoading, setEditPhotosLoading] = useState(false);
  const [editPhotosError, setEditPhotosError] = useState('');
  const [editPhotoOptions, setEditPhotoOptions] = useState([]);
  const [editSelectedPhotoUrl, setEditSelectedPhotoUrl] = useState('');

  // Link to PlantADay plant
  const [linkingItem, setLinkingItem] = useState(null);
  const [plantSearch, setPlantSearch] = useState('');
  const [plantResults, setPlantResults] = useState([]);
  const [plantSearching, setPlantSearching] = useState(false);

  const authHeader = useMemo(() => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const filterOptions = useMemo(() => {
    const norm = (v) => String(v || '').trim().toLowerCase();
    const build = (key) => {
      const map = new Map(); // norm -> label
      let hasMissing = false;
      for (const it of items || []) {
        const raw = it?.[key];
        const n = norm(raw);
        if (!n) {
          hasMissing = true;
          continue;
        }
        if (!map.has(n)) map.set(n, String(raw).trim());
      }
      return {
        values: Array.from(map.entries())
          .sort((a, b) => a[1].localeCompare(b[1], undefined, { sensitivity: 'base' }))
          .map(([value, label]) => ({ value, label })),
        hasMissing
      };
    };
    return {
      exposure: build('exposure'),
      type: build('plant_type'),
      colour: build('color')
    };
  }, [items]);

  const visibleItems = useMemo(() => {
    const norm = (v) => String(v || '').trim().toLowerCase();
    const isMissing = (v) => v == null || String(v).trim().length === 0;

    const matchesMissing = (it) => {
      switch (missingFilter) {
        case 'common_name':
          return isMissing(it.common_name);
        case 'botanical_name':
          return isMissing(it.botanical_name);
        case 'image_url':
          return isMissing(it.image_url);
        case 'code':
          return isMissing(it.code);
        default:
          return true;
      }
    };

    const matchesValueFilter = (it, filterValue, key) => {
      if (!filterValue) return true;
      if (filterValue === '__missing__') return isMissing(it?.[key]);
      return norm(it?.[key]) === filterValue;
    };

    return (items || []).filter((it) => {
      if (!matchesMissing(it)) return false;
      if (!matchesValueFilter(it, filterExposure, 'exposure')) return false;
      if (!matchesValueFilter(it, filterType, 'plant_type')) return false;
      if (!matchesValueFilter(it, filterColour, 'color')) return false;
      return true;
    });
  }, [items, missingFilter, filterExposure, filterType, filterColour]);

  const toggleSelectedId = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllVisible = (checked) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set((visibleItems || []).map((it) => String(it.id))));
  };

  const bulkApplySymbolToSelected = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Apply symbol settings to ${count} selected plant${count > 1 ? 's' : ''}? This will overwrite existing symbol settings.`)) {
      return;
    }
    const sizeIn = bulkSymbolSizeIn === '' ? null : Number(bulkSymbolSizeIn);
    setBulkApplyingSymbol(true);
    try {
      const debugPlacement = (() => {
        try {
          return localStorage.getItem('debugPlacement') === '1';
        } catch {
          return false;
        }
      })();
      const response = await fetch('/api/designer-master-plant-list/bulk-update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader, ...(debugPlacement ? { 'X-Placement-Debug': '1' } : {}) },
        body: JSON.stringify({
          ids: Array.from(selectedIds),
          symbol: bulkSymbol,
          symbol_size_in: Number.isFinite(sizeIn) ? sizeIn : null
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.details || 'Bulk update failed');
      setShowBulkSymbolModal(false);
      await fetchLibrary({ append: false, nextOffset: 0 });
      alert(`Updated ${data.updatedCount || 0} item(s).`);
    } catch (e) {
      console.error('bulkApplySymbolToSelected error:', e);
      alert(e.message || 'Failed to bulk apply symbol');
    } finally {
      setBulkApplyingSymbol(false);
    }
  };

  const exportLibraryToCsv = async () => {
    if (exportingCsv) return;
    const headers = [
      'ID',
      'Plant ID',
      'Common Name',
      'Botanical Name',
      'Quantity',
      'Unit Price',
      'Width',
      'Height',
      'Exposure',
      'Plant Type',
      'Code',
      'Pot Size',
      'Colour',
      'Notes',
      'Image URL',
      'Reference URL'
    ];
    const escapeCsv = (value) => {
      if (value == null) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    try {
      setExportingCsv(true);
      setError('');

      // Export should include the full library (not just the currently loaded page).
      const all = [];
      const limit = 500;
      let exportOffset = 0;
      while (true) {
        const qs = new URLSearchParams();
        if (search && search.trim()) qs.set('search', search.trim());
        qs.set('limit', String(limit));
        qs.set('offset', String(exportOffset));
        const response = await fetch(`/api/designer-master-plant-list?${qs.toString()}`, {
          headers: { ...authHeader }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Failed to export');
        const batch = Array.isArray(data.items) ? data.items : [];
        all.push(...batch);
        if (batch.length < limit) break;
        exportOffset += batch.length;
      }

      if (all.length === 0) {
        alert('No items to export!');
        return;
      }

      const rows = all.map((it) => [
        escapeCsv(it.id),
        escapeCsv(it.plant_id || ''),
        escapeCsv(it.common_name || ''),
        escapeCsv(it.botanical_name || ''),
        it.quantity ?? '',
        it.unit_price ?? '',
        it.width ?? '',
        it.height ?? '',
        escapeCsv(it.exposure || ''),
        escapeCsv(it.plant_type || ''),
        escapeCsv(it.code || ''),
        escapeCsv(it.pot_size || ''),
        escapeCsv(it.color || ''),
        escapeCsv(it.notes || ''),
        escapeCsv(it.image_url || ''),
        escapeCsv(it.reference_url || '')
      ]);

      const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `master_plant_list_library_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export CSV error:', e);
      alert(`Failed to export CSV: ${e.message}`);
    } finally {
      setExportingCsv(false);
    }
  };

  const fetchLibrary = async ({ append = false, nextOffset = 0 } = {}) => {
    try {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', String(pageSize));
      params.set('offset', String(nextOffset));

      const response = await fetch(`/api/designer-master-plant-list?${params.toString()}`, {
        headers: {
          ...authHeader
        }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch master plant list');
      }
      const data = await response.json();
      const batch = Array.isArray(data.items) ? data.items : [];
      const nextTotal = Number.isFinite(Number(data.total_count)) ? Number(data.total_count) : 0;
      setItems((prev) => (append ? [...prev, ...batch] : batch));
      setOffset(nextOffset + batch.length);
      setTotalCount(nextTotal);
      setHasMore(nextOffset + batch.length < nextTotal);
    } catch (e) {
      console.error('Error fetching designer master plant list:', e);
      setError(e.message || 'Failed to load your Master Plant List');
      if (!append) {
        setItems([]);
        setOffset(0);
        setHasMore(false);
        setTotalCount(0);
      }
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const dedupeByBotanical = async () => {
    if (deduping) return;
    const ok = window.confirm(
      'Remove duplicates by Botanical Name from your Master Plant List?\n\nThis keeps the newest item per botanical name and removes the others (best-effort merging missing fields).'
    );
    if (!ok) return;
    try {
      setDeduping(true);
      const response = await fetch('/api/designer-master-plant-list/dedupe-botanical', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({})
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const parts = [
          data.error || 'Failed to remove duplicates',
          data.step ? `step=${data.step}` : '',
          data.pg?.code ? `pg=${data.pg.code}` : '',
          data.details || ''
        ].filter(Boolean);
        throw new Error(parts.join(' | '));
      }
      alert(
        `Done.\n\nGroups: ${data.groups ?? 0}\nRemoved: ${data.deleted ?? 0}\nProject links updated: ${data.reassigned_project_items ?? 0}`
      );
      await fetchLibrary({ append: false, nextOffset: 0 });
    } catch (e) {
      console.error('Dedupe error:', e);
      alert(`Failed to remove duplicates: ${e.message}`);
    } finally {
      setDeduping(false);
    }
  };

  const consolidateFromProjects = async ({ silent = false } = {}) => {
    try {
      setConsolidating(true);
      const response = await fetch('/api/designer-master-plant-list/consolidate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to consolidate from projects');
      }
      await fetchLibrary();
    } catch (e) {
      console.error('Consolidate error:', e);
      if (!silent) {
        alert(`Failed to refresh from projects: ${e.message}`);
      }
    } finally {
      setConsolidating(false);
    }
  };

  useEffect(() => {
    // First load: best-effort consolidate once so historical projects populate the library.
    // Don't block the page if consolidate fails (schema differences between environments).
    (async () => {
      await fetchLibrary({ append: false, nextOffset: 0 });
      await consolidateFromProjects({ silent: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Search refresh
    const t = setTimeout(() => {
      fetchLibrary({ append: false, nextOffset: 0 });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pageSize]);

  // Keep local search state in sync if user lands via URL param (?search=...)
  useEffect(() => {
    if (initialSearch !== search) {
      setSearch(initialSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearch]);

  // Auto-open edit modal if navigated with ?editItem=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editItemId = params.get('editItem');
    if (!editItemId) return;
    if (didAutoEditRef.current) return;
    if (!items || items.length === 0) return;

    const target = items.find((it) => String(it.id) === String(editItemId));
    if (!target) return;

    didAutoEditRef.current = true;
    startEdit(target);

    setTimeout(() => {
      try {
        const el = document.getElementById(`dml-row-${target.id}`);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch {
        // ignore
      }
    }, 50);

    // Clean URL
    try {
      params.delete('editItem');
      const next = params.toString();
      navigate(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, location.search]);

  const parseCsvFile = async (file) => {
    setCsvParsing(true);
    setCsvImportStep(1);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = String(e.target.result || '');
        const records = parse(text, {
          columns: true,
          skip_empty_lines: true,
          trim: true,
          relax_column_count: true,
          relax_quotes: true
        });
        if (!Array.isArray(records) || records.length === 0) {
          setCsvHeaders([]);
          setCsvPreview([]);
          setCsvColumnMapping(emptyCsvColumnMapping());
          alert('No data rows found in this CSV.');
          setCsvParsing(false);
          return;
        }
        const headers = Object.keys(records[0] || {});
        setCsvHeaders(headers);
        setCsvPreview(records);
        setCsvColumnMapping(suggestCsvColumnMapping(headers));
        setCsvImportStep(2);
      } catch (err) {
        console.error('CSV parse error', err);
        alert(`Could not read CSV: ${err.message || err}`);
        setCsvHeaders([]);
        setCsvPreview([]);
        setCsvColumnMapping(emptyCsvColumnMapping());
      } finally {
        setCsvParsing(false);
      }
    };
    reader.onerror = (err) => {
      console.error('CSV read error', err);
      setCsvParsing(false);
    };
    reader.readAsText(file);
  };

  const importCsvToLibrary = async () => {
    if (!csvPreview.length) return;

    const mapped = csvPreview
      .map((row) => rowToLibraryItemFromMapping(row, csvColumnMapping))
      .filter((r) => {
        // Only skip rows that are completely empty (allow partial/incomplete rows).
        const keys = [
          'plant_id',
          'common_name',
          'botanical_name',
          'pot_size',
          'color',
          'unit_price',
          'width',
          'height',
          'exposure',
          'plant_type',
          'code',
          'image_url',
          'notes',
          'reference_url'
        ];
        return keys.some((k) => {
          const v = r[k];
          if (v === null || v === undefined) return false;
          const s = String(v).trim();
          return s.length > 0;
        });
      });

    if (mapped.length === 0) {
      alert('No valid rows found (all rows were empty).');
      return;
    }

    try {
      setImporting(true);
      const response = await fetch('/api/designer-master-plant-list/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({ items: mapped, source: 'csv' })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Import failed');
      }

      const data = await response.json();
      alert(
        `Import complete!\n✅ Created: ${data.created || 0}\n✏️ Updated: ${data.updated || 0}\n🟰 Duplicates: ${data.duplicates || 0}\n❌ Failed: ${data.failed || 0}`
      );

      setShowCsvModal(false);
      setCsvFile(null);
      setCsvHeaders([]);
      setCsvPreview([]);
      setCsvImportStep(1);
      setCsvColumnMapping(emptyCsvColumnMapping());
      await fetchLibrary();
    } catch (e) {
      console.error('Import error:', e);
      alert(`Failed to import: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const createItem = async () => {
    const hasAnyField = [
      newValues.plant_id,
      newValues.common_name,
      newValues.botanical_name,
      newValues.pot_size,
      newValues.color,
      newValues.unit_price,
      newValues.width,
      newValues.height,
      newValues.exposure,
      newValues.plant_type,
      newValues.code,
      newValues.notes,
      newValues.image_url,
      newValues.reference_url
    ].some((v) => v != null && String(v).trim().length > 0);
    if (!hasAnyField) {
      alert('Please fill at least one field.');
      return;
    }

    const payload = {
      ...newValues,
      unit_price: newValues.unit_price === '' ? null : parseFloat(newValues.unit_price),
      width: newValues.width === '' ? null : parseFloat(newValues.width),
      height: newValues.height === '' ? null : parseFloat(newValues.height),
      quantity: newValues.quantity === '' ? null : parseInt(newValues.quantity, 10)
    };

    try {
      setCreating(true);
      const response = await fetch('/api/designer-master-plant-list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.details || 'Failed to create item');
      }
      const data = await response.json();
      if (data.duplicate) {
        alert('That exact item already exists in your Master Plant List.');
      }
      setShowAddModal(false);
      setNewValues({
        plant_id: null,
        common_name: '',
        botanical_name: '',
        pot_size: '',
        color: '',
        unit_price: '',
        width: '',
        height: '',
        exposure: '',
        plant_type: '',
        code: '',
        quantity: 1,
        notes: '',
        image_url: '',
        reference_url: ''
      });
      setAddSearchTerm('');
      setAddSearchResults([]);
      setAddSelectedPlant(null);
      setAddPhotosError('');
      setAddPhotoOptions([]);
      setAddSelectedPhotoUrl('');
      setAiLookupQuery('');
      setAiLookupError('');
      setAiDraftPlant(null);
      setAiSmartResults([]);
      setAiPhotosError('');
      setAiPhotoOptions([]);
      setAiSelectedPhotoUrl('');
      await fetchLibrary();
    } catch (e) {
      console.error('Create item error:', e);
      alert(`Failed to create: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const openGoogleImages = (query) => {
    const q = (query || '').toString().trim();
    if (!q) return;
    window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(q)}`, '_blank', 'noopener,noreferrer');
  };

  const searchPlantsForAdd = async (query) => {
    const q = (query || '').toString().trim();
    if (q.length < 2) {
      setAddSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`/api/plants?search=${encodeURIComponent(q)}&limit=20`, {
        headers: {
          ...authHeader
        }
      });
      if (!response.ok) throw new Error('Failed to search plants');
      const data = await response.json();
      setAddSearchResults(data.plants || data || []);
    } catch (e) {
      console.error('Add modal plant search error:', e);
      setAddSearchResults([]);
    }
  };

  const fetchAddPhotos = async ({ botanicalName, commonName }) => {
    const b = (botanicalName || '').trim();
    const c = (commonName || '').trim();
    if (!b && !c) return;
    setAddPhotosLoading(true);
    setAddPhotosError('');
    setAddPhotoOptions([]);
    try {
      const verifiedResp = await fetch('/api/plants/find-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: true })
      });
      const verifiedData = await verifiedResp.json().catch(() => ({}));
      const verifiedPhotos = Array.isArray(verifiedData.photos) ? verifiedData.photos : [];

      let photos = verifiedPhotos;
      if (photos.length === 0) {
        const anyResp = await fetch('/api/plants/find-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: false })
        });
        const anyData = await anyResp.json().catch(() => ({}));
        photos = Array.isArray(anyData.photos) ? anyData.photos : [];
      }

      setAddPhotoOptions(photos);
      if (photos.length > 0) {
        const first = photos[0].url;
        setAddSelectedPhotoUrl((prev) => prev || first);
        setNewValues((prev) => {
          if (prev.image_url && String(prev.image_url).trim()) return prev;
          return { ...prev, image_url: first };
        });
      }
    } catch (e) {
      console.error('Error fetching add photos:', e);
      setAddPhotosError(e.message || 'Failed to find photos');
    } finally {
      setAddPhotosLoading(false);
    }
  };

  const selectAddPhoto = (photo) => {
    if (!photo?.url) return;
    setAddSelectedPhotoUrl(photo.url);
    setNewValues((prev) => ({ ...prev, image_url: photo.url }));
  };

  const fetchEditPhotos = async ({ botanicalName, commonName }) => {
    const b = (botanicalName || '').trim();
    const c = (commonName || '').trim();
    if (!b && !c) return;
    setEditPhotosLoading(true);
    setEditPhotosError('');
    setEditPhotoOptions([]);
    try {
      const verifiedResp = await fetch('/api/plants/find-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: true })
      });
      const verifiedData = await verifiedResp.json().catch(() => ({}));
      const verifiedPhotos = Array.isArray(verifiedData.photos) ? verifiedData.photos : [];

      let photos = verifiedPhotos;
      if (photos.length === 0) {
        const anyResp = await fetch('/api/plants/find-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: false })
        });
        const anyData = await anyResp.json().catch(() => ({}));
        photos = Array.isArray(anyData.photos) ? anyData.photos : [];
      }

      setEditPhotoOptions(photos);
      if (photos.length > 0) {
        const first = photos[0].url;
        setEditSelectedPhotoUrl((prev) => prev || first);
        setEditValues((prev) => {
          if (prev.image_url && String(prev.image_url).trim()) return prev;
          return { ...prev, image_url: first };
        });
      }
    } catch (e) {
      console.error('Error fetching edit photos:', e);
      setEditPhotosError(e.message || 'Failed to find photos');
    } finally {
      setEditPhotosLoading(false);
    }
  };

  const selectEditPhoto = (photo) => {
    if (!photo?.url) return;
    setEditSelectedPhotoUrl(photo.url);
    setEditValues((prev) => ({ ...prev, image_url: photo.url }));
  };

  const fetchAiPhotos = async ({ botanicalName, commonName }) => {
    const b = (botanicalName || '').trim();
    const c = (commonName || '').trim();
    if (!b && !c) return;
    setAiPhotosLoading(true);
    setAiPhotosError('');
    setAiPhotoOptions([]);
    try {
      const verifiedResp = await fetch('/api/plants/find-photos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: true })
      });
      const verifiedData = await verifiedResp.json().catch(() => ({}));
      const verifiedPhotos = Array.isArray(verifiedData.photos) ? verifiedData.photos : [];

      let photos = verifiedPhotos;
      if (photos.length === 0) {
        const anyResp = await fetch('/api/plants/find-photos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader },
          body: JSON.stringify({ botanicalName: b || undefined, commonName: c || undefined, verifiedOnly: false })
        });
        const anyData = await anyResp.json().catch(() => ({}));
        photos = Array.isArray(anyData.photos) ? anyData.photos : [];
      }

      setAiPhotoOptions(photos);
      if (photos.length > 0) {
        setAiSelectedPhotoUrl((prev) => prev || photos[0].url);
      }
    } catch (e) {
      console.error('Error fetching AI photos:', e);
      setAiPhotosError(e.message || 'Failed to find photos');
    } finally {
      setAiPhotosLoading(false);
    }
  };

  const runAiLookup = async () => {
    const q = aiLookupQuery.trim();
    if (!q) return;
    setAiLookupLoading(true);
    setAiLookupError('');
    setAiDraftPlant(null);
    setAiSmartResults([]);
    setAiPhotosError('');
    setAiPhotoOptions([]);
    setAiSelectedPhotoUrl('');
    try {
      const response = await fetch('/api/plants/ai-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ query: q })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = (data.error || data.message || data.details || '').toString();
        const looksDescriptive = msg.toLowerCase().includes('descriptive') || msg.toLowerCase().includes('smart-search');
        if (looksDescriptive) {
          const smartResp = await fetch('/api/plants/smart-search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader },
            body: JSON.stringify({ query: q, limit: 8 })
          });
          const smartData = await smartResp.json().catch(() => ({}));
          if (!smartResp.ok) throw new Error(smartData.error || smartData.details || 'AI smart search failed');
          setAiSmartResults(smartData.plants || []);
          if ((smartData.plants || []).length > 0) {
            const first = smartData.plants[0];
            setAiDraftPlant({
              common_name: first.common_name,
              botanical_name: first.botanical_name,
              plant_type: first.plant_type || '',
              exposure: first.exposure || '',
              width: first.width ?? null,
              height: first.height ?? null,
              notes: '',
              image_url: first.image_url || ''
            });
            await fetchAiPhotos({ botanicalName: first.botanical_name, commonName: first.common_name });
          }
          return;
        }
        throw new Error(data.error || data.details || 'AI lookup failed');
      }

      const plant = data.plant || data;
      const draft = {
        common_name: plant.common_name || plant.commonName || q,
        botanical_name: plant.botanical_name || plant.botanicalName || '',
        plant_type: plant.plant_type || plant.plantType || '',
        exposure: plant.exposure || '',
        width: plant.width ?? null,
        height: plant.height ?? null,
        notes: '',
        image_url: ''
      };
      setAiDraftPlant(draft);
      await fetchAiPhotos({ botanicalName: draft.botanical_name, commonName: draft.common_name });
    } catch (e) {
      console.error('AI lookup error:', e);
      setAiLookupError(e.message || 'AI lookup failed');
    } finally {
      setAiLookupLoading(false);
    }
  };

  const selectAiResult = async (plant) => {
    if (!plant) return;
    const draft = {
      common_name: plant.common_name || '',
      botanical_name: plant.botanical_name || '',
      plant_type: plant.plant_type || '',
      exposure: plant.exposure || '',
      width: plant.width ?? null,
      height: plant.height ?? null,
      notes: '',
      image_url: plant.image_url || ''
    };
    setAiDraftPlant(draft);
    setAiSelectedPhotoUrl(draft.image_url || '');
    await fetchAiPhotos({ botanicalName: draft.botanical_name, commonName: draft.common_name });
  };

  const addAiPlantToLibrary = async () => {
    if (!aiDraftPlant) return;
    try {
      setAddingAIPlant(true);
      const imageUrl = aiSelectedPhotoUrl || aiDraftPlant.image_url || '';
      const createResp = await fetch('/api/plants/add-custom', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          ...aiDraftPlant,
          image_url: imageUrl || null
        })
      });
      const createData = await createResp.json().catch(() => ({}));
      if (!createResp.ok) throw new Error(createData.error || createData.details || 'Failed to create private plant');

      const createdPlant = createData.plant || createData;
      const libResp = await fetch('/api/designer-master-plant-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          plant_id: createdPlant.id,
          common_name: createdPlant.common_name || aiDraftPlant.common_name,
          botanical_name: createdPlant.botanical_name || aiDraftPlant.botanical_name,
          pot_size: '',
          color: '',
          unit_price: null,
          width: aiDraftPlant.width ?? null,
          height: aiDraftPlant.height ?? null,
          exposure: aiDraftPlant.exposure || '',
          plant_type: aiDraftPlant.plant_type || '',
          code: '',
          notes: aiDraftPlant.notes || '',
          image_url: createdPlant.image_url || imageUrl || null,
          reference_url: null,
          source: 'ai'
        })
      });
      const libData = await libResp.json().catch(() => ({}));
      if (!libResp.ok) throw new Error(libData.error || libData.details || 'Failed to add plant to library');

      setShowAddModal(false);
      setAiLookupQuery('');
      setAiLookupError('');
      setAiDraftPlant(null);
      setAiSmartResults([]);
      setAiPhotosError('');
      setAiPhotoOptions([]);
      setAiSelectedPhotoUrl('');
      await fetchLibrary();
    } catch (e) {
      console.error('Add AI plant error:', e);
      alert(`Failed to add AI plant: ${e.message}`);
    } finally {
      setAddingAIPlant(false);
    }
  };

  const startEdit = (item) => {
    setEditing(item);
    setEditPhotosError('');
    setEditPhotoOptions([]);
    setEditPhotosLoading(false);
    setEditSelectedPhotoUrl(item.image_url || '');
    setEditValues({
      plant_id: item.plant_id || null,
      common_name: item.common_name || '',
      botanical_name: item.botanical_name || '',
      pot_size: item.pot_size || '',
      color: item.color || '',
      unit_price: item.unit_price ?? '',
      width: item.width ?? '',
      height: item.height ?? '',
      exposure: item.exposure || '',
      plant_type: item.plant_type || '',
      code: item.code || '',
      quantity: item.quantity ?? 1,
      notes: item.notes || '',
      image_url: item.image_url || '',
      reference_url: item.reference_url || '',
      symbol: item.symbol || 'circle',
      symbol_size_in: item.symbol_size_in ?? 0.5
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const hasAnyField = [
      editValues.plant_id,
      editValues.common_name,
      editValues.botanical_name,
      editValues.pot_size,
      editValues.color,
      editValues.unit_price,
      editValues.width,
      editValues.height,
      editValues.exposure,
      editValues.plant_type,
      editValues.code,
      editValues.notes,
      editValues.image_url,
      editValues.reference_url
    ].some((v) => v != null && String(v).trim().length > 0);
    if (!hasAnyField) {
      alert('Please fill at least one field.');
      return;
    }

    const payload = {
      ...editValues,
      unit_price: editValues.unit_price === '' ? null : parseFloat(editValues.unit_price),
      width: editValues.width === '' ? null : parseFloat(editValues.width),
      height: editValues.height === '' ? null : parseFloat(editValues.height),
      symbol: (editValues.symbol || '').toString().trim() || null,
      symbol_size_in: editValues.symbol_size_in === '' ? null : parseFloat(editValues.symbol_size_in),
      quantity: editValues.quantity === '' ? null : parseInt(editValues.quantity, 10)
    };

    try {
      const response = await fetch(`/api/designer-master-plant-list/${editing.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.details || 'Failed to save');
      }

      setEditing(null);
      setEditValues({});
      await fetchLibrary();
    } catch (e) {
      console.error('Save edit error:', e);
      alert(`Failed to save: ${e.message}`);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.common_name}" from your Master Plant List?`)) return;
    try {
      const response = await fetch(`/api/designer-master-plant-list/${item.id}`, {
        method: 'DELETE',
        headers: {
          ...authHeader
        }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete');
      }
      await fetchLibrary();
    } catch (e) {
      console.error('Delete error:', e);
      alert(`Failed to delete: ${e.message}`);
    }
  };

  const searchPlants = async (query) => {
    if (!query || query.trim().length < 2) {
      setPlantResults([]);
      return;
    }
    try {
      setPlantSearching(true);
      const response = await fetch(`/api/plants?search=${encodeURIComponent(query.trim())}&limit=20`, {
        headers: {
          ...authHeader
        }
      });
      if (!response.ok) throw new Error('Failed to search plants');
      const data = await response.json();
      setPlantResults(data.plants || data || []);
    } catch (e) {
      console.error('Plant search error:', e);
      setPlantResults([]);
    } finally {
      setPlantSearching(false);
    }
  };

  const linkPlant = async (plant) => {
    if (!linkingItem) return;
    try {
      const response = await fetch(`/api/designer-master-plant-list/${linkingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({ plant_id: plant.id })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to link plant');
      }
      setLinkingItem(null);
      setPlantSearch('');
      setPlantResults([]);
      await fetchLibrary();
    } catch (e) {
      console.error('Link error:', e);
      alert(`Failed to link: ${e.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-none mx-auto px-3 sm:px-4 lg:px-6 py-6">
          <div className="flex justify-between items-start gap-4">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-3xl font-bold text-gray-900 mt-2">Master Plant List</h1>
              <p className="mt-1 text-sm text-gray-600 max-w-3xl">
                Your catalog built from plants across <strong>all your projects</strong>, plus CSV imports and manual adds.
                This is not the same as a single job&apos;s <strong>Project Plant List</strong> (that list lives on each project).
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Loaded <span className="font-medium">{items.length}</span> / Total{' '}
                <span className="font-medium">{totalCount}</span>
              </p>
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              {selectedIds.size > 0 && (
                <button
                  onClick={() => {
                    setBulkSymbol((prev) => prev || 'circle');
                    setBulkSymbolSizeIn('0.5');
                    setShowBulkSymbolModal(true);
                  }}
                  className="px-4 py-2 rounded-md shadow-sm text-sm font-semibold text-white bg-sky-600 hover:bg-sky-700"
                  title="Bulk set symbol and size for selected rows"
                >
                  Bulk Symbol ({selectedIds.size})
                </button>
              )}
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Add Plant
              </button>
              <button
                onClick={exportLibraryToCsv}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                title="Export your library to CSV (includes IDs for safe re-import updates)"
                disabled={exportingCsv}
              >
                {exportingCsv ? 'Exporting…' : 'Export CSV'}
              </button>
              <button
                onClick={consolidateFromProjects}
                disabled={consolidating}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {consolidating ? 'Refreshing…' : 'Refresh from Projects'}
              </button>
              <button
                onClick={dedupeByBotanical}
                disabled={deduping}
                className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                title="Remove duplicates by Botanical Name (Master Library only)"
              >
                {deduping ? 'Removing…' : 'Remove Duplicates'}
              </button>
              <button
                onClick={() => {
                  setCsvImportStep(1);
                  setCsvFile(null);
                  setCsvHeaders([]);
                  setCsvPreview([]);
                  setCsvColumnMapping(emptyCsvColumnMapping());
                  setShowCsvModal(true);
                }}
                className="px-4 py-2 rounded-md shadow-sm text-sm font-semibold text-white bg-green-600 hover:bg-green-700"
              >
                Import CSV
              </button>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex flex-wrap gap-2 items-center">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search (name, botanical, code, size, colour, reference link)…"
                className="w-full max-w-lg px-3 py-2 border rounded-md"
              />
              <select
                value={filterExposure}
                onChange={(e) => setFilterExposure(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                title="Filter by exposure"
              >
                <option value="">Exposure: Any</option>
                {filterOptions.exposure.hasMissing && <option value="__missing__">Exposure: (missing)</option>}
                {filterOptions.exposure.values.map((opt) => (
                  <option key={`expo-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                title="Filter by plant type"
              >
                <option value="">Type: Any</option>
                {filterOptions.type.hasMissing && <option value="__missing__">Type: (missing)</option>}
                {filterOptions.type.values.map((opt) => (
                  <option key={`type-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select
                value={filterColour}
                onChange={(e) => setFilterColour(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                title="Filter by colour"
              >
                <option value="">Colour: Any</option>
                {filterOptions.colour.hasMissing && <option value="__missing__">Colour: (missing)</option>}
                {filterOptions.colour.values.map((opt) => (
                  <option key={`col-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value, 10) || 200)}
                className="px-3 py-2 border rounded-md text-sm"
                title="How many rows to load per page"
              >
                <option value={100}>100 / page</option>
                <option value={200}>200 / page</option>
                <option value={500}>500 / page</option>
              </select>
              <select
                value={missingFilter}
                onChange={(e) => setMissingFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                title="Filter to show plants missing a specific field"
              >
                <option value="">Show all</option>
                <option value="common_name">Missing Common</option>
                <option value="botanical_name">Missing Botanical</option>
                <option value="image_url">Missing Image Link</option>
                <option value="code">Missing Code</option>
              </select>
              {(missingFilter || filterExposure || filterType || filterColour) && (
                <div className="text-xs text-gray-600">
                  Showing {visibleItems.length} of {items.length}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-none mx-auto px-3 sm:px-4 lg:px-6 py-6">
        {error && (
          <div className="mb-4 bg-red-50 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-gray-600">Loading…</div>
        ) : (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-1.5 py-2 w-10">
                      <input
                        type="checkbox"
                        checked={
                          visibleItems.length > 0 &&
                          visibleItems.every((it) => selectedIds.has(String(it.id)))
                        }
                        onChange={(e) => selectAllVisible(e.target.checked)}
                        title="Select all visible"
                      />
                    </th>
                    <th className="text-left px-1.5 py-2 w-12">Qty</th>
                    <th className="text-left px-1.5 py-2 w-[170px]">Common</th>
                    <th className="text-left px-1.5 py-2 w-[230px]">Botanical</th>
                    <th className="text-left px-1.5 py-2 w-16">Size</th>
                    <th className="text-left px-1.5 py-2 w-16">Col</th>
                    <th className="text-left px-1.5 py-2 w-16">Code</th>
                    <th className="hidden lg:table-cell text-left px-1.5 py-2 w-14">W</th>
                    <th className="hidden lg:table-cell text-left px-1.5 py-2 w-14">H</th>
                    <th className="hidden lg:table-cell text-left px-1.5 py-2 w-16">Expo</th>
                    <th className="hidden lg:table-cell text-left px-1.5 py-2 w-16">Type</th>
                    <th className="text-left px-1.5 py-2 w-12">Img</th>
                    <th className="text-left px-1.5 py-2 w-12">Ref</th>
                    <th className="sticky right-0 text-left px-1.5 py-2 w-24 bg-gray-50 shadow-[-1px_0_0_#e5e7eb]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((item) => (
                    <tr key={item.id} id={`dml-row-${item.id}`} className="border-t">
                      <td className="px-1.5 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(String(item.id))}
                          onChange={() => toggleSelectedId(item.id)}
                          title="Select row"
                        />
                      </td>
                      <td className="px-1.5 py-2">{item.quantity ?? 1}</td>
                      <td className="px-1.5 py-2 font-semibold truncate" title={item.common_name || ''}>
                        {item.common_name || <span className="text-red-600">(missing)</span>}
                      </td>
                      <td className="px-1.5 py-2 italic text-gray-700 truncate" title={item.botanical_name || ''}>
                        {item.botanical_name || '-'}
                      </td>
                      <td className="px-1.5 py-2">{item.pot_size || '-'}</td>
                      <td className="px-1.5 py-2">{item.color || '-'}</td>
                      <td className="px-1.5 py-2">{item.code || '-'}</td>
                      <td className="hidden lg:table-cell px-1.5 py-2">{item.width ?? '-'}</td>
                      <td className="hidden lg:table-cell px-1.5 py-2">{item.height ?? '-'}</td>
                      <td className="hidden lg:table-cell px-1.5 py-2">{item.exposure || '-'}</td>
                      <td className="hidden lg:table-cell px-1.5 py-2">{item.plant_type || '-'}</td>
                      <td className="px-1.5 py-2">
                        {item.image_url ? (
                          <a href={item.image_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                            Link
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-1.5 py-2">
                        {item.reference_url ? (
                          <a
                            href={item.reference_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                            title={item.reference_url}
                          >
                            Link
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="sticky right-0 px-1.5 py-2 bg-white shadow-[-1px_0_0_#e5e7eb]">
                        <div className="flex flex-wrap gap-1 justify-end">
                          <button
                            onClick={() => {
                              setLinkingItem(item);
                              setPlantSearch(item.botanical_name || item.common_name);
                              setPlantResults([]);
                            }}
                            className="p-1.5 border rounded hover:bg-gray-50"
                            title="Link to PlantADay plant"
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => startEdit(item)}
                            className="p-1.5 border rounded hover:bg-gray-50"
                            title="Edit (add photos, codes, sizes, etc.)"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteItem(item)}
                            className="p-1.5 border rounded text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {items.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-gray-600" colSpan={16}>
                        No items yet. Use “Import CSV” or “Refresh from Projects”.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {!loading && hasMore && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
                <div className="text-sm text-gray-600">
                  Loaded: {items.length}
                </div>
                <button
                  type="button"
                  onClick={() => fetchLibrary({ append: true, nextOffset: offset })}
                  disabled={loadingMore}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-60"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV Import Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-semibold mb-2">Import your Master Plant List from CSV</h3>
            <p className="text-sm text-gray-600 mb-3">
              Step {csvImportStep} of 2: choose a UTF-8 CSV, then match your column headers to library fields. Quoted fields
              and commas inside URLs are supported.
            </p>
            <details className="mb-4 text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 bg-gray-50">
              <summary className="cursor-pointer font-medium text-gray-800">Quick help</summary>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>
                  Create a project <strong>without a site plan</strong> from the dashboard new-project flow when you only
                  need collaboration and plant lists.
                </li>
                <li>
                  Use <strong>Reference URL</strong> for Google (or other) plant pages; use <strong>Image URL</strong> for
                  a direct photo link.
                </li>
                <li>
                  Full field notes live in the repo file <code className="text-xs">DESIGNER_CSV_IMPORT.md</code>.
                </li>
              </ul>
            </details>

            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                setCsvFile(file || null);
                if (file) parseCsvFile(file);
              }}
              className="mb-4 block w-full text-sm"
            />

            {csvParsing && <div className="text-center py-4">Parsing CSV…</div>}

            {csvImportStep >= 2 && csvHeaders.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Map columns</h4>
                <p className="text-xs text-gray-600 mb-3">
                  We guessed some mappings from your headers—adjust any row, then check the preview below.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 max-h-64 overflow-y-auto pr-1">
                  {CSV_IMPORT_FIELD_ORDER.map(({ key, label }) => (
                    <div key={key} className="flex flex-col">
                      <label className="text-xs font-medium text-gray-600 mb-0.5">{label}</label>
                      <select
                        className="border rounded px-2 py-1.5 text-sm"
                        value={csvColumnMapping[key] || ''}
                        onChange={(e) =>
                          setCsvColumnMapping((prev) => ({
                            ...prev,
                            [key]: e.target.value
                          }))
                        }
                      >
                        <option value="">— Skip —</option>
                        {csvHeaders.map((h) => (
                          <option key={`${key}-${h}`} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {csvImportStep >= 2 && csvPreview.length > 0 && (
              <div className="mb-4 max-h-56 overflow-y-auto border rounded p-2 text-sm">
                <div className="text-gray-600 mb-2">Mapped preview (first 25 of {csvPreview.length})</div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left">
                      <th className="pr-2">Common</th>
                      <th className="pr-2">Botanical</th>
                      <th className="pr-2">Code</th>
                      <th className="pr-2">Size</th>
                      <th className="pr-2">Ref</th>
                      <th className="pr-2">Img</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.slice(0, 25).map((row, idx) => {
                      const mapped = rowToLibraryItemFromMapping(row, csvColumnMapping);
                      return (
                        <tr key={idx} className="border-t">
                          <td className="py-1 pr-2">
                            {mapped.common_name || <span className="text-red-600">(missing)</span>}
                          </td>
                          <td className="py-1 pr-2 italic">{mapped.botanical_name || '-'}</td>
                          <td className="py-1 pr-2">{mapped.code || '-'}</td>
                          <td className="py-1 pr-2">{mapped.pot_size || '-'}</td>
                          <td className="py-1 pr-2">{mapped.reference_url ? 'yes' : '-'}</td>
                          <td className="py-1 pr-2">{mapped.image_url ? 'yes' : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  setShowCsvModal(false);
                  setCsvFile(null);
                  setCsvHeaders([]);
                  setCsvPreview([]);
                  setCsvImportStep(1);
                  setCsvColumnMapping(emptyCsvColumnMapping());
                }}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={importCsvToLibrary}
                disabled={csvImportStep < 2 || !csvPreview.length || importing}
                className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-400"
              >
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl p-6">
            <h3 className="text-xl font-semibold mb-4">Add Plant</h3>

            {/* Search Database */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search database</label>
              <input
                value={addSearchTerm}
                onChange={(e) => {
                  const v = e.target.value;
                  setAddSearchTerm(v);
                  searchPlantsForAdd(v);
                  if (!addSelectedPlant) {
                    setNewValues((prev) => ({ ...prev, common_name: v }));
                  }
                }}
                className="w-full px-3 py-2 border rounded"
                placeholder="e.g. English lavender"
                autoFocus
              />
              {addSearchResults.length > 0 && !addSelectedPlant && (
                <div className="mt-2 max-h-48 overflow-y-auto border rounded">
                  {addSearchResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setAddSelectedPlant(p);
                        setAddSearchTerm(p.common_name || '');
                        setNewValues((prev) => ({
                          ...prev,
                          plant_id: p.id,
                          common_name: p.common_name || prev.common_name,
                          botanical_name: p.botanical_name || prev.botanical_name,
                          image_url: p.image_url || prev.image_url
                        }));
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="font-semibold">{p.common_name}</div>
                      <div className="text-xs italic text-gray-600">{p.botanical_name}</div>
                    </button>
                  ))}
                </div>
              )}
              {addSelectedPlant && (
                <div className="mt-2 text-xs text-gray-600">
                  Selected: <span className="font-semibold">{addSelectedPlant.common_name}</span>{' '}
                  <button
                    type="button"
                    className="text-blue-600 hover:underline ml-2"
                    onClick={() => {
                      setAddSelectedPlant(null);
                      setAddSearchResults([]);
                      setNewValues((prev) => ({ ...prev, plant_id: null }));
                    }}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>

            {/* AI lookup */}
            <div className="mb-4 p-3 border rounded bg-gray-50">
              <div className="text-sm font-semibold mb-2">AI lookup</div>
              <div className="flex gap-2">
                <input
                  value={aiLookupQuery}
                  onChange={(e) => setAiLookupQuery(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded"
                  placeholder="e.g. lavender that stays small in full sun"
                />
                <button
                  type="button"
                  onClick={runAiLookup}
                  disabled={aiLookupLoading || !aiLookupQuery.trim()}
                  className="px-4 py-2 bg-purple-600 text-white rounded disabled:bg-gray-400"
                >
                  {aiLookupLoading ? 'Looking…' : 'Lookup'}
                </button>
              </div>
              {aiLookupError && <div className="text-xs text-red-700 mt-2">{aiLookupError}</div>}

              {aiSmartResults.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-gray-600 mb-1">Suggestions</div>
                  <div className="flex flex-wrap gap-2">
                    {aiSmartResults.slice(0, 6).map((p) => (
                      <button
                        key={p.id || `${p.common_name}-${p.botanical_name}`}
                        type="button"
                        onClick={() => selectAiResult(p)}
                        className="px-3 py-1.5 border rounded bg-white hover:bg-gray-50 text-sm"
                      >
                        {p.common_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {aiDraftPlant && (
                <div className="mt-3">
                  <div className="text-sm font-semibold">
                    {aiDraftPlant.common_name}{aiDraftPlant.botanical_name ? ` • ${aiDraftPlant.botanical_name}` : ''}
                  </div>
                  <div className="mt-2">
                    <div className="text-xs font-semibold text-gray-600 mb-1">Recommended photos</div>
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => fetchAiPhotos({ botanicalName: aiDraftPlant.botanical_name, commonName: aiDraftPlant.common_name })}
                        disabled={aiPhotosLoading}
                        className="px-3 py-2 bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        {aiPhotosLoading ? 'Finding…' : 'Find photos'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openGoogleImages(aiDraftPlant.botanical_name || aiDraftPlant.common_name)}
                        className="px-3 py-2 bg-white border rounded hover:bg-gray-50"
                      >
                        Google
                      </button>
                    </div>
                    {aiPhotosError && <div className="text-xs text-red-700 mb-2">{aiPhotosError}</div>}
                    {aiPhotoOptions.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '8px' }}>
                        {aiPhotoOptions.slice(0, 12).map((photo, idx) => (
                          <button
                            key={`${photo.url}-${idx}`}
                            type="button"
                            onClick={() => setAiSelectedPhotoUrl(photo.url)}
                            className="border rounded overflow-hidden hover:shadow-sm"
                            style={{ borderColor: aiSelectedPhotoUrl === photo.url ? '#7c3aed' : '#e5e7eb' }}
                          >
                            <div style={{ width: '100%', height: '56px', background: '#f3f4f6' }}>
                              <img
                                src={photo.thumbnail || photo.url}
                                alt={photo.alt || aiDraftPlant.common_name || 'Plant'}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={addAiPlantToLibrary}
                        disabled={addingAIPlant}
                        className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-400"
                      >
                        {addingAIPlant ? 'Adding…' : 'Add AI plant to my library'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Library fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Common Name *</label>
                <input
                  value={newValues.common_name}
                  onChange={(e) => setNewValues({ ...newValues, common_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Botanical Name</label>
                <input
                  value={newValues.botanical_name}
                  onChange={(e) => setNewValues({ ...newValues, botanical_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded italic"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Pot Size</label>
                <input
                  value={newValues.pot_size}
                  onChange={(e) => setNewValues({ ...newValues, pot_size: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Colour</label>
                <input
                  value={newValues.color}
                  onChange={(e) => setNewValues({ ...newValues, color: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Unit Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={newValues.unit_price}
                  onChange={(e) => setNewValues({ ...newValues, unit_price: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Code</label>
                <input
                  value={newValues.code}
                  onChange={(e) => setNewValues({ ...newValues, code: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Qty</label>
                <input
                  type="number"
                  min="1"
                  value={newValues.quantity}
                  onChange={(e) => setNewValues({ ...newValues, quantity: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Width</label>
                <input
                  type="number"
                  step="0.1"
                  value={newValues.width}
                  onChange={(e) => setNewValues({ ...newValues, width: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Height</label>
                <input
                  type="number"
                  step="0.1"
                  value={newValues.height}
                  onChange={(e) => setNewValues({ ...newValues, height: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Expo</label>
                <input
                  value={newValues.exposure}
                  onChange={(e) => setNewValues({ ...newValues, exposure: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
                <input
                  value={newValues.plant_type}
                  onChange={(e) => setNewValues({ ...newValues, plant_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Image</label>
                <div className="flex gap-2">
                  <input
                    value={newValues.image_url}
                    onChange={(e) => setNewValues({ ...newValues, image_url: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded"
                    placeholder="https://…"
                  />
                  <button
                    type="button"
                    onClick={() => fetchAddPhotos({ botanicalName: newValues.botanical_name, commonName: newValues.common_name })}
                    disabled={addPhotosLoading}
                    className="px-3 py-2 bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    {addPhotosLoading ? 'Finding…' : 'Find'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openGoogleImages(newValues.botanical_name || newValues.common_name)}
                    className="px-3 py-2 bg-white border rounded hover:bg-gray-50"
                  >
                    Google
                  </button>
                </div>
                {addPhotosError && <div className="text-xs text-red-700 mt-1">{addPhotosError}</div>}
                {addPhotoOptions.length > 0 && (
                  <div className="mt-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '8px' }}>
                    {addPhotoOptions.slice(0, 12).map((photo, idx) => (
                      <button
                        key={`${photo.url}-${idx}`}
                        type="button"
                        onClick={() => selectAddPhoto(photo)}
                        className="border rounded overflow-hidden hover:shadow-sm"
                        style={{ borderColor: addSelectedPhotoUrl === photo.url ? '#7c3aed' : '#e5e7eb' }}
                        title={photo.attribution || photo.source || 'Photo'}
                      >
                        <div style={{ width: '100%', height: '56px', background: '#f3f4f6' }}>
                          <img
                            src={photo.thumbnail || photo.url}
                            alt={photo.alt || newValues.common_name || 'Plant'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Reference URL</label>
                <input
                  value={newValues.reference_url || ''}
                  onChange={(e) => setNewValues({ ...newValues, reference_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="e.g. Google plant search link"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  value={newValues.notes}
                  onChange={(e) => setNewValues({ ...newValues, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddSelectedPlant(null);
                  setAddSearchResults([]);
                }}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={createItem}
                disabled={creating}
                className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-400"
              >
                {creating ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden">
            <div className="p-6 pb-3 border-b">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-xl font-semibold">Edit Library Item</h3>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setEditValues({});
                  }}
                  className="text-gray-500 hover:text-gray-800"
                  title="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 pt-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Common Name *</label>
                  <input
                    value={editValues.common_name || ''}
                    onChange={(e) => setEditValues({ ...editValues, common_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Botanical Name</label>
                  <input
                    value={editValues.botanical_name || ''}
                    onChange={(e) => setEditValues({ ...editValues, botanical_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded italic"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Pot Size</label>
                  <input
                    value={editValues.pot_size || ''}
                    onChange={(e) => setEditValues({ ...editValues, pot_size: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Colour</label>
                  <input
                    value={editValues.color || ''}
                    onChange={(e) => setEditValues({ ...editValues, color: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Unit Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editValues.unit_price}
                    onChange={(e) => setEditValues({ ...editValues, unit_price: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Code</label>
                  <input
                    value={editValues.code || ''}
                    onChange={(e) => setEditValues({ ...editValues, code: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Qty</label>
                  <input
                    type="number"
                    min="1"
                    value={editValues.quantity ?? 1}
                    onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Width</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editValues.width}
                    onChange={(e) => setEditValues({ ...editValues, width: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Height</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editValues.height}
                    onChange={(e) => setEditValues({ ...editValues, height: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Expo</label>
                  <input
                    value={editValues.exposure || ''}
                    onChange={(e) => setEditValues({ ...editValues, exposure: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
                  <input
                    value={editValues.plant_type || ''}
                    onChange={(e) => setEditValues({ ...editValues, plant_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Symbol</label>
                  <select
                    value={editValues.symbol || 'circle'}
                    onChange={(e) => setEditValues({ ...editValues, symbol: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    {allSymbolOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Symbol size (in)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editValues.symbol_size_in ?? 0.5}
                    onChange={(e) => setEditValues({ ...editValues, symbol_size_in: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Image URL</label>
                  <div className="flex gap-2">
                    <input
                      value={editValues.image_url || ''}
                      onChange={(e) => setEditValues({ ...editValues, image_url: e.target.value })}
                      className="flex-1 px-3 py-2 border rounded"
                      placeholder="https://…"
                    />
                    <button
                      type="button"
                      onClick={() => fetchEditPhotos({ botanicalName: editValues.botanical_name, commonName: editValues.common_name })}
                      disabled={editPhotosLoading}
                      className="px-3 py-2 bg-white border rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      {editPhotosLoading ? 'Finding…' : 'Find'}
                    </button>
                    <button
                      type="button"
                      onClick={() => openGoogleImages(editValues.botanical_name || editValues.common_name)}
                      className="px-3 py-2 bg-white border rounded hover:bg-gray-50"
                    >
                      Google
                    </button>
                  </div>
                  {editPhotosError && <div className="text-xs text-red-700 mt-1">{editPhotosError}</div>}
                  {editPhotoOptions.length > 0 && (
                    <div className="mt-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '8px' }}>
                      {editPhotoOptions.slice(0, 12).map((photo, idx) => (
                        <button
                          key={`${photo.url}-${idx}`}
                          type="button"
                          onClick={() => selectEditPhoto(photo)}
                          className="border rounded overflow-hidden hover:shadow-sm"
                          style={{ borderColor: editSelectedPhotoUrl === photo.url ? '#7c3aed' : '#e5e7eb' }}
                          title={photo.attribution || photo.source || 'Photo'}
                        >
                          <div style={{ width: '100%', height: '56px', background: '#f3f4f6' }}>
                            <img
                              src={photo.thumbnail || photo.url}
                              alt={photo.alt || editValues.common_name || 'Plant'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Reference URL</label>
                  <input
                    value={editValues.reference_url || ''}
                    onChange={(e) => setEditValues({ ...editValues, reference_url: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="e.g. Google plant search link"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                  <textarea
                    value={editValues.notes || ''}
                    onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="p-6 pt-4 border-t bg-white flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setEditValues({});
                }}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Symbol Modal */}
      {showBulkSymbolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-semibold">Bulk set symbol</h3>
                <div className="text-sm text-gray-600">
                  Applies to <span className="font-semibold">{selectedIds.size}</span> selected row(s). Overwrites existing symbol settings.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowBulkSymbolModal(false)}
                className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Symbol</label>
                <select
                  value={bulkSymbol}
                  onChange={(e) => setBulkSymbol(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {allSymbolOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Symbol size (in)</label>
                <input
                  type="number"
                  value={bulkSymbolSizeIn}
                  onChange={(e) => setBulkSymbolSizeIn(e.target.value)}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowBulkSymbolModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                disabled={bulkApplyingSymbol}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={bulkApplySymbolToSelected}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:bg-gray-400"
                disabled={bulkApplyingSymbol || selectedIds.size === 0}
              >
                {bulkApplyingSymbol ? 'Applying…' : 'Apply to selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {linkingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-xl p-6">
            <h3 className="text-xl font-semibold mb-2">Link to PlantADay Plant</h3>
            <p className="text-sm text-gray-600 mb-3">
              This does not create a public plant; it only links this library row to an existing PlantADay entry.
            </p>
            <input
              value={plantSearch}
              onChange={(e) => {
                setPlantSearch(e.target.value);
                searchPlants(e.target.value);
              }}
              placeholder="Search plants…"
              className="w-full px-3 py-2 border rounded"
              autoFocus
            />
            {plantSearching && <div className="py-3 text-sm text-gray-600">Searching…</div>}

            <div className="mt-3 max-h-72 overflow-y-auto border rounded">
              {plantResults.map((p) => (
                <button
                  key={p.id}
                  onClick={() => linkPlant(p)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                >
                  <div className="font-semibold">{p.common_name}</div>
                  <div className="text-xs italic text-gray-600">{p.botanical_name}</div>
                </button>
              ))}
              {plantResults.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-600">
                  Type at least 2 characters to search.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setLinkingItem(null);
                  setPlantSearch('');
                  setPlantResults([]);
                }}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DesignerMasterPlantList;

